import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// Create the Auth context
const AuthContext = createContext();

// Expiry time of a JWT in ms, or null if it can't be read.
const getTokenExpiry = (token) => {
  try {
    const payload = token.split('.')[1];
    const { exp } = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return exp ? exp * 1000 : null;
  } catch {
    return null;
  }
};

const isExpired = (token) => {
  const expiry = getTokenExpiry(token);
  return expiry !== null && expiry <= Date.now();
};

const readStoredToken = () => {
  const stored = localStorage.getItem('token');
  if (!stored || isExpired(stored)) {
    localStorage.removeItem('token');
    return null;
  }
  return stored;
};

const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(readStoredToken);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
  }, []);

  const login = useCallback((newToken) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
  }, []);

  // The token expires server-side after an hour. Drop it at that moment so the
  // app shows the login screen instead of leaving every request to fail with a
  // 401 while the UI still looks signed in.
  useEffect(() => {
    if (!token) return undefined;
    const expiry = getTokenExpiry(token);
    if (expiry === null) return undefined;

    const timer = setTimeout(logout, Math.max(expiry - Date.now(), 0));
    // Timers don't fire reliably while a tab is backgrounded or the machine
    // sleeps, so re-check whenever the tab becomes visible again.
    const recheck = () => {
      if (document.visibilityState === 'visible' && isExpired(token)) logout();
    };
    document.addEventListener('visibilitychange', recheck);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', recheck);
    };
  }, [token, logout]);

  const value = useMemo(
    () => ({ isAuthenticated: Boolean(token), token, login, logout }),
    [token, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const useAuth = () => useContext(AuthContext);

export { AuthProvider, useAuth };
